import type { Compare } from "~/tools/compare";
import { isPresent } from "~/tools/is-present";
import { isStrictEqual } from "~/tools/is-strict-equal";
import type { Record } from "~/tools/record";
import { toArray } from "~/tools/to-array";
import { toEntries } from "~/tools/to-entries";

export interface ImmutableMapOptions<TValue> {
	isValueEqual?: (left: TValue, right: TValue) => boolean;
}

export class ImmutableMap<TKey, TValue> implements ReadonlyMap<TKey, TValue> {
	public static fromRecord<TKey extends PropertyKey, TValue>(
		record: Record<TKey, null | undefined | TValue, "readonly", "optional">,
		options?: ImmutableMapOptions<TValue>,
	): ImmutableMap<TKey, TValue> {
		const entries = toEntries(record)
			.map(([key, value]) => (value == null ? null : ([key, value] as [TKey, TValue])))
			.filter(isPresent);

		return ImmutableMap.fromEntries(entries, options);
	}

	public static fromList<TKey, TValue>(
		toKey: (item: TValue, index: number) => TKey,
		list: ReadonlyArray<TValue>,
		options?: ImmutableMapOptions<TValue>,
	): ImmutableMap<TKey, TValue> {
		return ImmutableMap.fromEntries(
			list.map((item, index) => [toKey(item, index), item]),
			options,
		);
	}

	public static fromEntries<TKey, TValue>(
		entries: ReadonlyArray<[TKey, TValue]>,
		{ isValueEqual = isStrictEqual }: ImmutableMapOptions<TValue> = {},
	): ImmutableMap<TKey, TValue> {
		return new ImmutableMap(new Map(entries), isValueEqual);
	}

	private static readonly EMPTY_STRICT = new ImmutableMap(new Map<never, never>(), isStrictEqual);

	public static empty<TKey, TValue>({
		isValueEqual = isStrictEqual,
	}: ImmutableMapOptions<TValue> = {}): ImmutableMap<TKey, TValue> {
		if (isValueEqual === isStrictEqual) {
			return ImmutableMap.EMPTY_STRICT;
		}

		return new ImmutableMap(new Map<TKey, TValue>(), isValueEqual);
	}

	private constructor(
		private readonly map: ReadonlyMap<TKey, TValue>,
		private readonly compare?: Compare<TValue>,
	) {}

	private get isValueEqual() {
		return this.compare ?? isStrictEqual;
	}

	public get size(): number {
		return this.map.size;
	}

	public has(value: TKey): boolean {
		return this.map.has(value);
	}

	public get(key: TKey): undefined | TValue {
		return this.map.get(key);
	}

	/**
	 * Returns a new map without the specified keys.
	 * Returns the same instance if none of the keys exist.
	 *
	 * @param keys - One or more keys to remove.
	 * @returns A new ImmutableMap without the specified keys.
	 */
	public delete(...keys: ReadonlyArray<TKey>): ImmutableMap<TKey, TValue> {
		const clone = new Map(this.map);

		for (const key of keys) {
			clone.delete(key);
		}

		if (clone.size === this.size) {
			return this;
		}

		return new ImmutableMap(clone, this.compare);
	}

	public clear(): ImmutableMap<TKey, TValue> {
		if (this.size === 0) {
			return this;
		}

		return new ImmutableMap(new Map<TKey, TValue>(), this.compare);
	}

	public set(key: TKey, value: TValue): ImmutableMap<TKey, TValue> {
		if (this.has(key) && this.isValueEqual(this.map.get(key)!, value)) {
			return this;
		}

		const clone = new Map(this.map);

		return new ImmutableMap(clone.set(key, value), this.compare);
	}

	public update(key: TKey, setter: (value: undefined | TValue) => undefined | TValue): ImmutableMap<TKey, TValue> {
		const value = setter(this.get(key));

		if (value === undefined) {
			return this.delete(key);
		}

		return this.set(key, value);
	}

	public forEach(
		callbackfn: (value: TValue, key: TKey, map: ReadonlyMap<TKey, TValue>) => void,
		thisArg?: unknown,
	): void {
		this.map.forEach(callbackfn, thisArg);
	}

	public isEqual(another: ReadonlyMap<TKey, TValue> | ImmutableMap<TKey, TValue>): boolean {
		if (this === another) {
			return true;
		}
		if (this.size !== another.size) {
			return false;
		}

		for (const [key, value] of another.entries()) {
			if (!(this.has(key) && this.isValueEqual(this.get(key)!, value))) {
				return false;
			}
		}

		return true;
	}

	public entries(): MapIterator<[TKey, TValue]> {
		return this.map.entries();
	}

	public keys(): MapIterator<TKey> {
		return this.map.keys();
	}

	public values(): MapIterator<TValue> {
		return this.map.values();
	}

	public [Symbol.iterator](): MapIterator<[TKey, TValue]> {
		return this.map[Symbol.iterator]();
	}

	public toArray(): Array<[TKey, TValue]> {
		return toArray(this.map);
	}

	/**
	 * Returns a new map with only the entries matching the predicate.
	 * Returns the same instance if all entries match.
	 *
	 * @param predicate - Function called with each value and key.
	 * @returns A new ImmutableMap with the matching entries.
	 */
	public filter(predicate: (value: TValue, key: TKey) => boolean): ImmutableMap<TKey, TValue> {
		const acc = new Map<TKey, TValue>();

		for (const [key, value] of this.map) {
			if (predicate(value, key)) {
				acc.set(key, value);
			}
		}

		if (acc.size === this.size) {
			return this;
		}

		return new ImmutableMap(acc, this.compare);
	}

	/**
	 * Returns the first value matching the predicate, or undefined.
	 *
	 * @param predicate - Function called with each value and key.
	 * @returns The first matching value, or undefined if none match.
	 */
	public find(predicate: (value: TValue, key: TKey) => boolean): undefined | TValue {
		for (const [key, value] of this.map) {
			if (predicate(value, key)) {
				return value;
			}
		}

		return undefined;
	}

	/**
	 * Returns a new map with keys transformed.
	 * Colliding keys are deduplicated (last wins).
	 *
	 * @param fn - Function called with each key and value, returning the new key.
	 * @returns A new ImmutableMap with the transformed keys.
	 */
	public mapKeys<TNewKey>(fn: (key: TKey, value: TValue) => TNewKey): ImmutableMap<TNewKey, TValue> {
		const acc = new Map<TNewKey, TValue>();

		for (const [key, value] of this.map) {
			acc.set(fn(key, value), value);
		}

		return new ImmutableMap(acc, this.compare);
	}

	/**
	 * Returns a new map with values transformed.
	 *
	 * @param fn - Function called with each value and key, returning the new value.
	 * @returns A new ImmutableMap with the transformed values.
	 */
	public mapValues<TNewValue>(fn: (value: TValue, key: TKey) => TNewValue): ImmutableMap<TKey, TNewValue> {
		const acc = new Map<TKey, TNewValue>();

		for (const [key, value] of this.map) {
			acc.set(key, fn(value, key));
		}

		return new ImmutableMap(acc);
	}

	/**
	 * Merges another map into this one.
	 * Entries from the other map win on key conflict.
	 * Returns the same instance if the other map is empty.
	 *
	 * @param another - The map to merge into this one.
	 * @returns A new ImmutableMap with the combined entries.
	 */
	public union(another: ReadonlyMap<TKey, TValue> | ImmutableMap<TKey, TValue>): ImmutableMap<TKey, TValue> {
		if (another.size === 0) {
			return this;
		}

		const acc = new Map(this.map);

		for (const [key, value] of another) {
			acc.set(key, value);
		}

		return new ImmutableMap(acc, this.compare);
	}

	/**
	 * Subtracts the entries of another ImmutableMap from this ImmutableMap.
	 * If an entry in this map has the same key and value as an entry in the other map,
	 * it will be removed from the resulting map.
	 *
	 * @param another - The ImmutableMap to subtract from this map.
	 * @returns A new ImmutableMap with the entries subtracted.
	 */
	public subtract(another: ReadonlyMap<TKey, TValue> | ImmutableMap<TKey, TValue>): ImmutableMap<TKey, TValue> {
		if (this.size === 0 || another.size === 0) {
			return this;
		}

		const acc = new Map(this.map);

		const anotherIsValueEqual = another instanceof ImmutableMap ? another.isValueEqual : isStrictEqual;

		const compare = (left: TValue, right: TValue) => this.isValueEqual(left, right) || anotherIsValueEqual(left, right);

		for (const [key, value] of another) {
			if (this.has(key) && compare(acc.get(key)!, value)) {
				acc.delete(key);
			}
		}

		return new ImmutableMap(acc, this.compare ?? anotherIsValueEqual);
	}
}
